import type { SegmenterOutput, SpeechSegmenter, UtteranceBoundary } from './speechSegmenter'
import type { SpeakerLabel, Utterance } from './utterance'

/** Max time to wait for the last commit when stopping. It also ends the wait when the audio context is suspended. */
const FINISH_TIMEOUT_MS = 3000

/**
 * Passes the utterance boundaries of one segmenter input to its OpenAI session.
 * The segmenter only analyzes samples. This class adds what one OpenAI session needs:
 * - Its own view of a shared segmenter. With speaker attribution, one segmenter analyzes both speaker channels,
 *   because the end of one speaker's turn depends on the other channel. Each channel has its own feed and session.
 * - The right timing (see {@link flush}).
 * - The wait for the last commit when listening stops (see {@link finish}).
 *
 * Reports:
 * - `onStart` when speech starts. The two OpenAI sessions can return their texts in a different order
 *   than people spoke, so the transcript uses the start positions to keep the conversation order.
 * - `onCommit` when an utterance ends. The Realtime controller then sends the commit to OpenAI.
 */
export class UtteranceFeed {
  /** Boundaries found since the last {@link flush}. */
  private pending: UtteranceBoundary[] = []
  /** True when the segmenter made the last commit of a stop since the last {@link flush}. */
  private ended = false
  private resolveFinish: (() => void) | null = null
  /** Called when speech starts, so the transcript can reserve the place of the coming text. */
  onStart: ((utterance: Utterance) => void) | null = null
  /** Called when an utterance ended and its audio was sent, so the Realtime controller can commit it. */
  onCommit: ((utterance: Utterance) => void) | null = null

  /**
   * Connect one segmenter input to its OpenAI session.
   * `receivedFrame` returns the amount of audio that arrived so far,
   * including audio that still waits in a queue. Stop uses it to include the last words.
   * `speaker` labels the utterances of a speaker channel. It is undefined in ordinary transcription.
   */
  constructor(
    private readonly segmenter: SpeechSegmenter,
    private readonly input: number,
    private readonly receivedFrame: () => number,
    readonly speaker?: SpeakerLabel,
  ) {}

  /**
   * Start reporting boundaries when the OpenAI session is ready to receive commits,
   * or stop reporting them when the connection closes.
   * Boundaries that were not yet reported are discarded: they belong to the closed connection.
   * The other input is not affected, also during a reconnect.
   */
  setEnabled(enabled: boolean) {
    this.pending = []
    this.ended = false
    this.segmenter.setEnabled(this.input, enabled)
  }

  /** Keep the boundaries of this feed's input from one segmenter result, until the next {@link flush}. */
  accept({ boundaries, ended }: SegmenterOutput) {
    this.pending.push(...boundaries.filter(boundary => boundary.input === this.input))
    if (ended.includes(this.input)) this.ended = true
  }

  /**
   * Report the kept boundaries. Call this when OpenAI has received the analyzed audio.
   *
   * Timing: the capture app's audio is analyzed in the Web Audio callback that also writes the samples
   * into the media track sent to OpenAI. The browser sends these samples only after the callback returns.
   * A commit sent at once could reach OpenAI before the last samples of the utterance,
   * and the last words would move to the next transcript line.
   * So the capture app's feeds flush at the start of the next callback, when the samples are already on their way.
   * WebRTC timing still varies, so the split at OpenAI is approximate.
   */
  flush() {
    const boundaries = this.pending
    this.pending = []
    for (const { type, utterance } of boundaries) {
      const labeled = { ...utterance, speaker: this.speaker }
      if (type === 'start') this.onStart?.(labeled)
      else this.onCommit?.(labeled)
    }
    if (this.ended) this.resolveFinish?.()
  }

  /**
   * Commit the last utterance of this input when listening stops, and wait until OpenAI has received its audio.
   * The last utterance includes all audio that arrived before the stop, also audio that is still in the queue.
   * The other input stops independently.
   */
  async finish() {
    if (!this.segmenter.finish(this.input, this.receivedFrame())) return
    await new Promise<void>(resolve => {
      const timeout = setTimeout(resolve, FINISH_TIMEOUT_MS)
      this.resolveFinish = () => {
        clearTimeout(timeout)
        resolve()
      }
    })
    this.resolveFinish = null
  }
}
